import { IncomeType, VerifyMethodType } from "shared/types";
import {
  CombinedIncome,
  VerificationSpecificAdditionaData
} from "./types/income";
import { BankStatements, CombinedIncomeReport, TotalSummary } from "./types";
import { toMoney } from "shared/utils";
import type { ReactNode } from "react";
import { getValueByPath } from "shared/app/VerificationTemplate/utils";
import {
  AdditionalData,
  VerifiedBankStatement
} from "./types/verified/bank-statment";
import { ReverifyAttempt } from "./slices/ReverifySlice/types";
import { isReverifyTabAPlaceholder } from "./slices/ReverifySlice/utils";

export const incomeTypeMap = {
  self_employment: "Self-employment",
  employment: "Employment",
  other: "Other",
  housing_assistance: "Housing assistance",
  offer_letter: "Offer letter"
};

export const verificationSourceType = {
  [VerifyMethodType.Bank]: "Bank account",
  [VerifyMethodType.Paystub]: "Paystub",
  [VerifyMethodType.BankStatement]: "Bank statement",
  [VerifyMethodType.W2]: "W2",
  [VerifyMethodType.Payroll]: "Payroll provider",
  [VerifyMethodType.OfferLetter]: "Offer letter",
  [VerifyMethodType.BenefitsStatement]: "Government benefit letter"
};

export const getVerificationTitleName = (income: CombinedIncome) => {
  switch (income.type) {
    case "employment":
      return income.additional_data.company_name || "Employment";
    case "self_employment":
      return income.additional_data.company_name || "Self-employment";
    case "other":
      return income.additional_data.paid_from || "Other";
    case "housing_assistance":
      return "Housing assistance";
    case "offer_letter":
      return income.additional_data?.company_name || "Offer letter";
    default:
      return "Unknown income type";
  }
};

export const getIncomeJobName = (
  additional_data: CombinedIncome["additional_data"]
) => {
  if (!additional_data) return "Unknown";

  if ("company_name" in additional_data && additional_data.company_name)
    return additional_data.company_name;

  if ("job_title" in additional_data && additional_data.job_title)
    return additional_data.job_title;

  if ("description" in additional_data && additional_data.description)
    return additional_data.description;

  if ("paid_from" in additional_data && additional_data.paid_from)
    return additional_data.paid_from;

  return "Unknown";
};

export const getMonthlyAmount = (
  amountCents: number,
  payPeriod: string
): number => {
  switch (payPeriod) {
    case "weekly":
      return (amountCents * 52) / 12;
    case "bi_weekly":
      return (amountCents * 26) / 12;
    case "annually":
      return amountCents / 12;
    default:
      return amountCents; // Assuming monthly if not specified
  }
};

export const isIncomeVerificationSkipped = (
  income: CombinedIncome
): boolean => {
  const additionalData = income.additional_data;
  const hasSkippedFlag = Boolean(
    additionalData &&
    "document_verification_skipped" in additionalData &&
    additionalData.document_verification_skipped
  );
  const hasNoVerificationData = !income.items.length;

  return Boolean(hasSkippedFlag && hasNoVerificationData);
};

export const getAdditionalDataPerVerificationType = (
  report?: CombinedIncomeReport,
  income?: CombinedIncome,
  bankStatements?: BankStatements
): VerificationSpecificAdditionaData | null => {
  if (!report || !income?.verified_source_type) return null;

  const additionalVerificationData: VerificationSpecificAdditionaData = {};

  if (report.bank_statements)
    additionalVerificationData[VerifyMethodType.BankStatement] = {
      allBankStatements: bankStatements,
      companyName: getIncomeJobName(income.additional_data),
      incomeId: income.id
    };

  if (income.verified_source_type === "benefits_statement")
    additionalVerificationData[VerifyMethodType.BenefitsStatement] = {
      status: income.status
    };

  return additionalVerificationData;
};

export const getIncomeOriginalNetAndGrossDollars = (
  income: CombinedIncome,
  selectedReverifyAttempt?: ReverifyAttempt
) => {
  let originalNetMonthlyIncomeDollars =
    income.verified_monthly_amounts.net || 0;
  let originalGrossMonthlyIncomeDollars =
    income.verified_monthly_amounts.gross || 0;

  if (
    selectedReverifyAttempt &&
    !isReverifyTabAPlaceholder(selectedReverifyAttempt) &&
    !Array.isArray(selectedReverifyAttempt) &&
    selectedReverifyAttempt.data
  ) {
    if ("net_monthly_income" in selectedReverifyAttempt.data)
      originalNetMonthlyIncomeDollars =
        (selectedReverifyAttempt.data.net_monthly_income?.cents || 0) / 100;
    else if ("net_monthly_income_amount" in selectedReverifyAttempt.data)
      originalNetMonthlyIncomeDollars =
        selectedReverifyAttempt.data.net_monthly_income_amount || 0;

    if ("gross_monthly_income" in selectedReverifyAttempt.data)
      originalGrossMonthlyIncomeDollars =
        (selectedReverifyAttempt.data.gross_monthly_income?.cents || 0) / 100;
    else if ("gross_monthly_income_amount" in selectedReverifyAttempt.data)
      originalGrossMonthlyIncomeDollars =
        selectedReverifyAttempt.data.gross_monthly_income_amount || 0;
  }

  return { originalNetMonthlyIncomeDollars, originalGrossMonthlyIncomeDollars };
};

const getIncomeOverrideNetAndGrossDollars = (income: CombinedIncome) => {
  const overridenNetMonthlyDollars = getValueByPath(income, [
    "additional_data",
    "overrides",
    "net_monthly_income"
  ]);
  const overridenGrossMonthlyDollars = getValueByPath(income, [
    "additional_data",
    "overrides",
    "gross_monthly_income"
  ]);

  return { overridenNetMonthlyDollars, overridenGrossMonthlyDollars };
};

export const getIncomeActualNetAndGrossDollars = (
  income: CombinedIncome,
  selectedReverifyAttempt?: ReverifyAttempt
) => {
  const { originalNetMonthlyIncomeDollars, originalGrossMonthlyIncomeDollars } =
    getIncomeOriginalNetAndGrossDollars(income, selectedReverifyAttempt);
  const { overridenNetMonthlyDollars, overridenGrossMonthlyDollars } =
    getIncomeOverrideNetAndGrossDollars(income);

  const actualVerifiedNetMonthlyDollars =
    typeof overridenNetMonthlyDollars === "number"
      ? overridenNetMonthlyDollars
      : originalNetMonthlyIncomeDollars;

  const actualVerifiedGrossMonthlyDollars =
    typeof overridenGrossMonthlyDollars === "number"
      ? overridenGrossMonthlyDollars
      : originalGrossMonthlyIncomeDollars;

  return {
    actualVerifiedNetMonthlyDollars,
    actualVerifiedGrossMonthlyDollars
  };
};

export const getNetMonthlyIncome = (
  report: CombinedIncomeReport,
  summary: TotalSummary
) =>
  report.total_overrides?.net_monthly_income !== undefined
    ? toMoney(report.total_overrides.net_monthly_income)
    : toMoney(summary.verified_net_monthly_total);

export const getDebtGrossIncomeRatio = (summary: TotalSummary) =>
  typeof summary.debt_gross_income_ratio === "undefined" ||
  summary.debt_gross_income_ratio === null
    ? "-"
    : summary.debt_gross_income_ratio + "%";

export const getSelfReportedGrossMonthly = (
  report: CombinedIncomeReport,
  summary: TotalSummary
) =>
  report.total_overrides?.gross_monthly_income !== undefined
    ? toMoney(report.total_overrides.gross_monthly_income)
    : toMoney(summary.self_reported_gross_monthly_total);

export const getVerifiedIncomeSourcesCount = (
  incomes: CombinedIncome[],
  summary: TotalSummary
) =>
  summary.verified_incomes_counter.verified +
  " / " +
  (summary.verified_incomes_counter.total +
    incomes.filter((inc) => inc.type === IncomeType.OfferLetter).length);

export const showValueOrPlaceholder = (
  value: number | string | ReactNode,
  condition?: boolean
) => {
  if (condition !== undefined) return condition ? value : " - ";
  return value || " - ";
};

const hasBankStatementBlockingRejection = (
  additionalData: AdditionalData | null
): boolean => {
  if (!additionalData) return false;

  return [
    additionalData.type_mismatch === true,
    !additionalData.bank_statement_start_date ||
      !additionalData.bank_statement_end_date,
    !additionalData.earliest_transaction_date &&
      !additionalData.latest_transaction_date,
    additionalData.is_name_present === false,
    additionalData.no_available_transactions === true,
    additionalData.low_quality_score === true
  ].some(Boolean);
};

export const isValidBankStatement = (
  bankStatement: VerifiedBankStatement
): boolean => {
  const { items, additional_data } = bankStatement;

  const hasItems = items.length > 0;
  const hasNonZeroItem = items.some(
    (item) => item.data.transaction.amount !== 0
  );
  const hasNoBlockingRejection =
    !hasBankStatementBlockingRejection(additional_data);

  return hasItems && hasNonZeroItem && hasNoBlockingRejection;
};
